/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Chip, Tooltip, IconButton } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { ConsumerProofOfDelivery } from '@/api/bol-notifications';
import { 
  getHttpStatusChipColor, 
  truncateText, 
  getNotificationCount 
} from '@/utils/datagridFormatters';

export const defaultConsumerProofOfDeliveryColumnVisibilityModel: Record<string, boolean> = {
  id: true,
  httpStatusCode: true,
  requestSentAt: true,
  responseReceivedAt: true,
  retryAttempts: true,
  responseBody: false,
  traceparentHeader: false,
  notificationPayload: false,
};

export const createConsumerProofOfDeliveryColumns = (
  onPayloadClick?: (payload: string) => void
): GridColDef<ConsumerProofOfDelivery>[] => [
  {
    field: 'id',
    headerName: 'ID',
    width: 280,
    flex: 1,
    renderCell: (params: any) => (
      <Tooltip title={params.value}>
        <span>{(params.value)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'httpStatusCode',
    headerName: 'Status Code',
    headerAlign: 'center',
    align: 'center',
    width: 130,
    flex: 1,
    renderCell: (params: any) => (
      <Chip 
        label={params.value} 
        size="small" 
        color={getHttpStatusChipColor(params.value)} 
      />
    ),
  },
  {
    field: 'requestSentAt',
    headerName: 'Request Sent At',
    width: 180,
    flex: 1,
    valueFormatter: (value: any) => value ? new Date(value).toLocaleString() : '-',
  },
  {
    field: 'responseReceivedAt',
    headerName: 'Response Received At',
    width: 200,
    flex: 1,
    valueFormatter: (value: any) => value ? new Date(value).toLocaleString() : '-',
  },
  {
    field: 'retryAttempts',
    headerName: 'Retry Attempts',
    headerAlign: 'center',
    align: 'center',
    width: 140,
    flex: 1,
    renderCell: (params: any) => (
      <Chip 
        label={params.value} 
        size="small" 
        color={params.value > 0 ? 'warning' : 'default'} 
      />
    ),
  },
  {
    field: 'responseBody',
    headerName: 'Response Body',
    width: 250,
    flex: 1,
    renderCell: (params: any) => (
      <Tooltip title={params.value || 'No response body'}>
        <span>{truncateText(params.value, 50)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'traceparentHeader',
    headerName: 'Traceparent',
    width: 320,
    flex: 1,
    renderCell: (params: any) => (
      <Tooltip title={params.value || 'No traceparent'}>
        <span>{truncateText(params.value, 40)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'notificationPayload',
    headerName: 'Notification Payload',
    headerAlign: 'center',
    align: 'center',
    width: 150,
    flex: 1,
    renderCell: (params: any) => {
      const payload = params.value;
      const count = getNotificationCount(payload);
      
      if (!payload || !onPayloadClick || count === 0) {
        return <span>-</span>;
      }

      return (
        <Tooltip title={`View ${count} notification${count !== 1 ? 's' : ''}`}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onPayloadClick(payload);
            }}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    },
  },
];
